/**
 * @file pages/SchedulePage.js
 * @description Schedule Butler — calendar, day view, list view,
 * and appointment booking. Responsive, touch-friendly.
 */
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../hooks/useTheme';
import AuthContext from '../contexts/AuthContext';
import { Modal } from '../design-system';

const API = process.env.REACT_APP_API_URL || '/api';

async function authFetch(url, options = {}) {
  const opts = {
    ...options,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) }
  };
  let response = await fetch(url, opts);
  if (response.status === 401) {
    const refreshRes = await fetch(`${API}/auth/refresh`, { method: 'POST', credentials: 'include' });
    if (refreshRes.ok) response = await fetch(url, opts);
  }
  return response;
}

// ============================================================
// STATUS COLORS
// ============================================================
const STATUS_COLORS = {
  requested: { bg: '#FEF3C7', border: '#F59E0B', text: '#92400E' },
  confirmed: { bg: '#DBEAFE', border: '#3B82F6', text: '#1E40AF' },
  in_progress: { bg: '#EDE9FE', border: '#8B5CF6', text: '#5B21B6' },
  completed: { bg: '#DCFCE7', border: '#22C55E', text: '#166534' },
  cancelled: { bg: '#FEE2E2', border: '#EF4444', text: '#991B1B' },
  rescheduled: { bg: '#FFEDD5', border: '#F97316', text: '#9A3412' }
};

const STATUS_LABELS = {
  requested: 'Requested',
  confirmed: 'Confirmed',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
  rescheduled: 'Rescheduled'
};

// ============================================================
// HELPER: format time
// ============================================================
function formatTime(timeStr) {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return hour + ':' + String(m).padStart(2, '0') + ' ' + ampm;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

// ============================================================
// MAIN COMPONENT
// ============================================================
function SchedulePage() {
  const auth = useContext(AuthContext);
  const { theme } = useTheme();
  const navigate = useNavigate();

  const [view, setView] = useState('month'); // month | day | list
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewForm, setShowNewForm] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [inspectors, setInspectors] = useState([]);

  // ---- Fetch calendar events ----
  const fetchEvents = useCallback(async (start, end) => {
    try {
      setLoading(true);
      const res = await authFetch(
        `${API}/schedule/calendar?start=${start}&end=${end}`
      );
      if (res.ok) {
        const json = await res.json();
        setEvents(json.data || []);
      }
    } catch (err) {
      console.error('Failed to load calendar:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // ---- Compute date range for current view ----
  useEffect(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    if (view === 'month') {
      const start = new Date(year, month, 1);
      const end = new Date(year, month + 1, 0);
      // Extend to full weeks
      start.setDate(start.getDate() - start.getDay());
      end.setDate(end.getDate() + (6 - end.getDay()));
      fetchEvents(
        start.toISOString().split('T')[0],
        end.toISOString().split('T')[0]
      );
    } else if (view === 'day') {
      const dateStr = currentDate.toISOString().split('T')[0];
      fetchEvents(dateStr, dateStr);
    } else {
      // list — next 30 days
      const end = new Date(currentDate);
      end.setDate(end.getDate() + 30);
      fetchEvents(
        currentDate.toISOString().split('T')[0],
        end.toISOString().split('T')[0]
      );
    }
  }, [currentDate, view, fetchEvents]);

  // ---- Fetch templates and inspectors for booking form ----
  useEffect(() => {
    async function loadMeta() {
      try {
        const [tRes, uRes] = await Promise.all([
          authFetch(`${API}/templates`),
          authFetch(`${API}/auth/users`)
        ]);
        if (tRes.ok) {
          const tj = await tRes.json();
          setTemplates(tj.data || tj || []);
        }
        if (uRes.ok) {
          const uj = await uRes.json();
          const users = uj.data || uj || [];
          setInspectors(users.filter(u =>
            u.role === 'inspector' || u.role === 'admin' || u.role === 'company_admin' || u.role === 'manager'
          ));
        }
      } catch (err) {
        console.error('Failed to load metadata:', err);
      }
    }
    loadMeta();
  }, []);

  // ---- Navigation ----
  function prevMonth() {
    setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  }
  function nextMonth() {
    setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  }
  function prevDay() {
    setCurrentDate(d => { const n = new Date(d); n.setDate(n.getDate() - 1); return n; });
  }
  function nextDay() {
    setCurrentDate(d => { const n = new Date(d); n.setDate(n.getDate() + 1); return n; });
  }
  function goToday() {
    setCurrentDate(new Date());
  }

  // ---- Event handlers ----
  async function handleStartInspection(apptId) {
    try {
      const res = await authFetch(`${API}/schedule/appointments/${apptId}/start`, { method: 'PUT' });
      if (res.ok) {
        const json = await res.json();
        setShowDetail(null);
        navigate('/inspections/' + json.data.inspection.id);
      } else {
        const err = await res.json();
        alert(err.error?.message || 'Failed to start inspection');
      }
    } catch (err) {
      alert('Failed to start inspection');
    }
  }

  async function handleCancelAppointment(apptId, reason) {
    try {
      const res = await authFetch(`${API}/schedule/appointments/${apptId}/cancel`, {
        method: 'PUT',
        body: JSON.stringify({ reason })
      });
      if (res.ok) {
        setShowDetail(null);
        // Refresh
        setCurrentDate(new Date(currentDate));
      }
    } catch (err) {
      alert('Failed to cancel appointment');
    }
  }

  // ============================================================
  // RENDER: Month Calendar
  // ============================================================
  function renderMonthCalendar() {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDay = new Date(firstDay);
    startDay.setDate(startDay.getDate() - startDay.getDay());

    const weeks = [];
    const day = new Date(startDay);

    while (day <= lastDay || day.getDay() !== 0) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        week.push(new Date(day));
        day.setDate(day.getDate() + 1);
      }
      weeks.push(week);
      if (day > lastDay && day.getDay() === 0) break;
    }

    const today = new Date().toISOString().split('T')[0];

    return (
      <div style={{ overflowX: 'auto' }}>
        <div style={styles.calendarGrid}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
            <div key={d} style={styles.calendarDayHeader}>{d}</div>
          ))}
          {weeks.map((week, wi) =>
            week.map((d, di) => {
              const dateStr = d.toISOString().split('T')[0];
              const isToday = dateStr === today;
              const isCurrentMonth = d.getMonth() === month;
              const dayEvents = events.filter(e =>
                (e.date === dateStr) ||
                (e.startDateTime && e.startDateTime.startsWith(dateStr))
              );

              return (
                <div
                  key={wi + '-' + di}
                  style={{
                    ...styles.calendarCell,
                    backgroundColor: isToday ? 'var(--color-primary-50, #EFF6FF)' : 'var(--surface-card, #fff)',
                    opacity: isCurrentMonth ? 1 : 0.4,
                    cursor: 'pointer',
                    borderColor: isToday ? 'var(--color-primary-500, #3B82F6)' : 'var(--color-border, #E5E7EB)'
                  }}
                  onClick={() => {
                    setSelectedDate(dateStr);
                    setCurrentDate(d);
                    setView('day');
                  }}
                >
                  <div style={{
                    ...styles.calendarDateNum,
                    fontWeight: isToday ? 700 : 400,
                    color: isToday ? 'var(--color-primary-600, #2563EB)' : 'var(--text-primary, #111)'
                  }}>
                    {d.getDate()}
                  </div>
                  <div style={styles.calendarEvents}>
                    {dayEvents.slice(0, 3).map(e => (
                      <div
                        key={e.id}
                        style={{
                          ...styles.calendarEventDot,
                          backgroundColor: e.color || '#3B82F6',
                          color: '#fff'
                        }}
                        title={e.title}
                        onClick={(ev) => {
                          ev.stopPropagation();
                          if (e.type === 'appointment') setShowDetail(e.data);
                        }}
                      >
                        {e.type === 'appointment'
                          ? formatTime(e.startTime)
                          : e.title
                        }
                      </div>
                    ))}
                    {dayEvents.length > 3 && (
                      <div style={{ fontSize: '0.65rem', color: 'var(--text-secondary, #666)', padding: '1px 4px' }}>
                        +{dayEvents.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  }

  // ============================================================
  // RENDER: Day Timeline
  // ============================================================
  function renderDayView() {
    const dateStr = currentDate.toISOString().split('T')[0];
    const dayEvents = events.filter(e =>
      (e.date === dateStr) ||
      (e.startDateTime && e.startDateTime.startsWith(dateStr))
    );

    const hours = [];
    for (let h = 6; h <= 20; h++) hours.push(h);

    return (
      <div style={styles.dayViewContainer}>
        <div style={styles.dayTimeline}>
          {hours.map(h => {
            const hourStr = String(h).padStart(2, '0');
            const label = h > 12 ? (h - 12) + ' PM' : (h === 12 ? '12 PM' : h + ' AM');

            // Find events in this hour
            const hourEvents = dayEvents.filter(e => {
              if (e.type === 'appointment') {
                const startH = parseInt(e.startTime?.split(':')[0] || 0);
                return startH === h;
              }
              return false;
            });

            return (
              <div key={h} style={styles.dayHourRow}>
                <div style={styles.dayHourLabel}>{label}</div>
                <div style={styles.dayHourSlot}>
                  {hourEvents.map(e => {
                    const colors = STATUS_COLORS[e.status] || STATUS_COLORS.confirmed;
                    return (
                      <div
                        key={e.id}
                        style={{
                          ...styles.dayEventBlock,
                          backgroundColor: colors.bg,
                          borderLeftColor: colors.border,
                          color: colors.text
                        }}
                        onClick={() => setShowDetail(e.data)}
                      >
                        <strong>{formatTime(e.startTime)} - {formatTime(e.endTime)}</strong>
                        <div>{e.title}</div>
                        <div style={{ fontSize: '0.75rem', opacity: 0.8 }}>
                          {STATUS_LABELS[e.status] || e.status}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ============================================================
  // RENDER: List View
  // ============================================================
  function renderListView() {
    const appointments = events
      .filter(e => e.type === 'appointment')
      .sort((a, b) => {
        if (a.date !== b.date) return a.date < b.date ? -1 : 1;
        return (a.startTime || '') < (b.startTime || '') ? -1 : 1;
      });

    if (appointments.length === 0) {
      return (
        <div style={styles.emptyState}>
          <h3>No upcoming appointments</h3>
          <p>Book an appointment and it will show up here.</p>
        </div>
      );
    }

    return (
      <div style={styles.listContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Date</th>
              <th style={styles.th}>Time</th>
              <th style={styles.th}>Customer</th>
              <th style={styles.thHideMobile}>Address</th>
              <th style={styles.th}>Status</th>
              <th style={styles.thHideMobile}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {appointments.map(e => {
              const colors = STATUS_COLORS[e.status] || STATUS_COLORS.confirmed;
              return (
                <tr
                  key={e.id}
                  style={{ ...styles.tr, cursor: 'pointer' }}
                  onClick={() => setShowDetail(e.data)}
                >
                  <td style={styles.td}>{formatDate(e.date)}</td>
                  <td style={styles.td}>{formatTime(e.startTime)}</td>
                  <td style={styles.td}>{e.data?.customerName || '-'}</td>
                  <td style={styles.tdHideMobile}>{e.data?.propertyAddress || '-'}</td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.badge,
                      backgroundColor: colors.bg,
                      color: colors.text,
                      borderColor: colors.border
                    }}>
                      {STATUS_LABELS[e.status] || e.status}
                    </span>
                  </td>
                  <td style={styles.tdHideMobile}>
                    {(e.status === 'confirmed' || e.status === 'requested') && (
                      <button
                        style={styles.btnSmall}
                        onClick={(ev) => { ev.stopPropagation(); handleStartInspection(e.id); }}
                      >
                        Start
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  // ============================================================
  // RENDER: Appointment Detail Modal
  // ============================================================
  function renderDetailModal() {
    if (!showDetail) return null;
    const a = showDetail;
    const colors = STATUS_COLORS[a.status] || STATUS_COLORS.confirmed;

    return (
      <Modal isOpen={true} onClose={() => setShowDetail(null)} title="Appointment Details">
        <div style={{ padding: '1rem' }}>
          <div style={{
            ...styles.badge,
            backgroundColor: colors.bg, color: colors.text,
            borderColor: colors.border, marginBottom: '1rem', display: 'inline-block'
          }}>
            {STATUS_LABELS[a.status] || a.status}
          </div>

          <div style={styles.detailGrid}>
            <div style={styles.detailGroup}>
              <label style={styles.detailLabel}>Date & Time</label>
              <div>{formatDate(a.scheduledDate)}</div>
              <div>{formatTime(a.scheduledStartTime)} - {formatTime(a.scheduledEndTime)}</div>
            </div>

            <div style={styles.detailGroup}>
              <label style={styles.detailLabel}>Customer</label>
              <div><strong>{a.customerName}</strong></div>
              {a.customerEmail && <div>{a.customerEmail}</div>}
              {a.customerPhone && <div>{a.customerPhone}</div>}
            </div>

            <div style={styles.detailGroup}>
              <label style={styles.detailLabel}>Property</label>
              <div>{a.propertyAddress}</div>
              {a.propertyCity && <div>{a.propertyCity}, {a.propertyState} {a.propertyZip}</div>}
            </div>

            {a.agentName && (
              <div style={styles.detailGroup}>
                <label style={styles.detailLabel}>Real Estate Agent</label>
                <div>{a.agentName}</div>
                {a.agentEmail && <div>{a.agentEmail}</div>}
                {a.agentPhone && <div>{a.agentPhone}</div>}
              </div>
            )}

            {a.price && (
              <div style={styles.detailGroup}>
                <label style={styles.detailLabel}>Price</label>
                <div>${parseFloat(a.price).toFixed(2)}</div>
              </div>
            )}

            {a.notes && (
              <div style={styles.detailGroup}>
                <label style={styles.detailLabel}>Notes</label>
                <div>{a.notes}</div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem', flexWrap: 'wrap' }}>
            {(a.status === 'confirmed' || a.status === 'requested') && (
              <button
                style={{ ...styles.btn, backgroundColor: 'var(--color-primary-600, #2563EB)', color: '#fff' }}
                onClick={() => handleStartInspection(a.id)}
              >
                Start Inspection
              </button>
            )}
            {a.status === 'requested' && (
              <button
                style={{ ...styles.btn, backgroundColor: 'var(--color-success-600, #16A34A)', color: '#fff' }}
                onClick={async () => {
                  await authFetch(`${API}/schedule/appointments/${a.id}/confirm`, { method: 'PUT' });
                  setShowDetail(null);
                  setCurrentDate(new Date(currentDate));
                }}
              >
                Confirm
              </button>
            )}
            {a.status !== 'completed' && a.status !== 'cancelled' && (
              <button
                style={{ ...styles.btn, backgroundColor: 'var(--color-error-600, #DC2626)', color: '#fff' }}
                onClick={() => {
                  const reason = prompt('Cancellation reason (optional):');
                  if (reason !== null) handleCancelAppointment(a.id, reason);
                }}
              >
                Cancel
              </button>
            )}
            {a.inspectionId && (
              <button
                style={{ ...styles.btn, backgroundColor: 'var(--color-primary-600, #2563EB)', color: '#fff' }}
                onClick={() => navigate('/inspections/' + a.inspectionId)}
              >
                View Inspection
              </button>
            )}
          </div>
        </div>
      </Modal>
    );
  }

  // ============================================================
  // RENDER: New Appointment Form
  // ============================================================
  function renderNewAppointmentForm() {
    return (
      <Modal isOpen={showNewForm} onClose={() => setShowNewForm(false)} title="New Appointment">
        <NewAppointmentForm
          templates={templates}
          inspectors={inspectors}
          onSave={async (data) => {
            try {
              const res = await authFetch(`${API}/schedule/appointments`, {
                method: 'POST',
                body: JSON.stringify(data)
              });
              if (res.ok) {
                setShowNewForm(false);
                setCurrentDate(new Date(currentDate));
              } else {
                const err = await res.json();
                alert(err.error?.message || 'Failed to book appointment');
              }
            } catch (err) {
              alert('Failed to book appointment');
            }
          }}
          onCancel={() => setShowNewForm(false)}
          selectedDate={selectedDate}
        />
      </Modal>
    );
  }

  // ============================================================
  // MAIN RENDER
  // ============================================================
  const monthName = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });
  const dayName = currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <div className="page-content" style={{ padding: '1rem', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h2 style={{ margin: 0 }}>Schedule</h2>
          <div style={styles.viewToggle}>
            {['month', 'day', 'list'].map(v => (
              <button
                key={v}
                onClick={() => setView(v)}
                style={{
                  ...styles.viewBtn,
                  backgroundColor: view === v ? 'var(--color-primary-600, #2563EB)' : 'transparent',
                  color: view === v ? '#fff' : 'var(--text-primary, #333)',
                }}
              >
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
        </div>
        <div style={styles.headerRight}>
          <button style={styles.todayBtn} onClick={goToday}>Today</button>
          <button
            style={{ ...styles.btn, backgroundColor: 'var(--color-primary-600, #2563EB)', color: '#fff' }}
            onClick={() => setShowNewForm(true)}
          >
            + New Appointment
          </button>
        </div>
      </div>

      {/* Navigation */}
      <div style={styles.navBar}>
        <button style={styles.navBtn} onClick={view === 'day' ? prevDay : prevMonth}>
          &larr;
        </button>
        <h3 style={{ margin: 0, minWidth: '200px', textAlign: 'center' }}>
          {view === 'day' ? dayName : monthName}
        </h3>
        <button style={styles.navBtn} onClick={view === 'day' ? nextDay : nextMonth}>
          &rarr;
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary, #666)' }}>
          Loading...
        </div>
      ) : (
        <>
          {view === 'month' && renderMonthCalendar()}
          {view === 'day' && renderDayView()}
          {view === 'list' && renderListView()}
        </>
      )}

      {/* Modals */}
      {renderDetailModal()}
      {renderNewAppointmentForm()}
    </div>
  );
}

// ============================================================
// NEW APPOINTMENT FORM COMPONENT
// ============================================================
function NewAppointmentForm({ templates, inspectors, onSave, onCancel, selectedDate }) {
  const [form, setForm] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    propertyAddress: '',
    propertyCity: '',
    propertyState: '',
    propertyZip: '',
    scheduledDate: selectedDate || new Date().toISOString().split('T')[0],
    scheduledStartTime: '09:00',
    estimatedDuration: 180,
    inspectionType: '',
    inspectorId: '',
    price: '',
    notes: '',
    source: 'phone',
    agentName: '',
    agentEmail: '',
    agentPhone: ''
  });

  const [showAgent, setShowAgent] = useState(false);
  const [saving, setSaving] = useState(false);

  function handleChange(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    const data = { ...form };
    if (data.price) data.price = parseFloat(data.price);
    if (data.estimatedDuration) data.estimatedDuration = parseInt(data.estimatedDuration);
    if (!data.inspectorId) delete data.inspectorId;
    if (!data.inspectionType) delete data.inspectionType;
    await onSave(data);
    setSaving(false);
  }

  const inputStyle = styles.input;
  const labelStyle = styles.formLabel;

  return (
    <form onSubmit={handleSubmit} style={{ padding: '1rem' }}>
      {/* Customer Info */}
      <fieldset style={styles.fieldset}>
        <legend style={styles.legend}>Customer</legend>
        <div style={styles.formRow}>
          <div style={styles.formField}>
            <label style={labelStyle}>Name *</label>
            <input style={inputStyle} value={form.customerName}
              onChange={e => handleChange('customerName', e.target.value)} required autoFocus />
          </div>
          <div style={styles.formField}>
            <label style={labelStyle}>Phone</label>
            <input style={inputStyle} type="tel" value={form.customerPhone}
              onChange={e => handleChange('customerPhone', e.target.value)} />
          </div>
        </div>
        <div style={styles.formField}>
          <label style={labelStyle}>Email</label>
          <input style={inputStyle} type="email" value={form.customerEmail}
            onChange={e => handleChange('customerEmail', e.target.value)} />
        </div>
      </fieldset>

      {/* Property */}
      <fieldset style={styles.fieldset}>
        <legend style={styles.legend}>Property</legend>
        <div style={styles.formField}>
          <label style={labelStyle}>Address *</label>
          <input style={inputStyle} value={form.propertyAddress}
            onChange={e => handleChange('propertyAddress', e.target.value)} required />
        </div>
        <div style={styles.formRow}>
          <div style={{ ...styles.formField, flex: 2 }}>
            <label style={labelStyle}>City</label>
            <input style={inputStyle} value={form.propertyCity}
              onChange={e => handleChange('propertyCity', e.target.value)} />
          </div>
          <div style={styles.formField}>
            <label style={labelStyle}>State</label>
            <input style={inputStyle} value={form.propertyState} maxLength={2}
              onChange={e => handleChange('propertyState', e.target.value.toUpperCase())} />
          </div>
          <div style={styles.formField}>
            <label style={labelStyle}>Zip</label>
            <input style={inputStyle} value={form.propertyZip} maxLength={10}
              onChange={e => handleChange('propertyZip', e.target.value)} />
          </div>
        </div>
      </fieldset>

      {/* Schedule */}
      <fieldset style={styles.fieldset}>
        <legend style={styles.legend}>Schedule</legend>
        <div style={styles.formRow}>
          <div style={styles.formField}>
            <label style={labelStyle}>Date *</label>
            <input style={inputStyle} type="date" value={form.scheduledDate}
              onChange={e => handleChange('scheduledDate', e.target.value)} required />
          </div>
          <div style={styles.formField}>
            <label style={labelStyle}>Start Time *</label>
            <input style={inputStyle} type="time" value={form.scheduledStartTime}
              onChange={e => handleChange('scheduledStartTime', e.target.value)} required />
          </div>
          <div style={styles.formField}>
            <label style={labelStyle}>Duration (min)</label>
            <select style={inputStyle} value={form.estimatedDuration}
              onChange={e => handleChange('estimatedDuration', e.target.value)}>
              <option value="60">1 hour</option>
              <option value="90">1.5 hours</option>
              <option value="120">2 hours</option>
              <option value="150">2.5 hours</option>
              <option value="180">3 hours</option>
              <option value="240">4 hours</option>
              <option value="300">5 hours</option>
              <option value="360">6 hours</option>
            </select>
          </div>
        </div>
        <div style={styles.formRow}>
          <div style={styles.formField}>
            <label style={labelStyle}>Inspection Type</label>
            <select style={inputStyle} value={form.inspectionType}
              onChange={e => handleChange('inspectionType', e.target.value)}>
              <option value="">-- Select Template --</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          {inspectors.length > 1 && (
            <div style={styles.formField}>
              <label style={labelStyle}>Inspector</label>
              <select style={inputStyle} value={form.inspectorId}
                onChange={e => handleChange('inspectorId', e.target.value)}>
                <option value="">-- Assign to me --</option>
                {inspectors.map(i => (
                  <option key={i.id} value={i.id}>
                    {i.firstName} {i.lastName}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </fieldset>

      {/* Details */}
      <fieldset style={styles.fieldset}>
        <legend style={styles.legend}>Details</legend>
        <div style={styles.formRow}>
          <div style={styles.formField}>
            <label style={labelStyle}>Price ($)</label>
            <input style={inputStyle} type="number" step="0.01" min="0" value={form.price}
              onChange={e => handleChange('price', e.target.value)} placeholder="0.00" />
          </div>
          <div style={styles.formField}>
            <label style={labelStyle}>Source</label>
            <select style={inputStyle} value={form.source}
              onChange={e => handleChange('source', e.target.value)}>
              <option value="phone">Phone Call</option>
              <option value="website">Website</option>
              <option value="referral">Referral</option>
              <option value="repeat">Repeat Customer</option>
              <option value="agent">Real Estate Agent</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>
        <div style={styles.formField}>
          <label style={labelStyle}>Notes</label>
          <textarea style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} value={form.notes}
            onChange={e => handleChange('notes', e.target.value)}
            placeholder="Special instructions, access codes, etc." />
        </div>
      </fieldset>

      {/* Agent Info (collapsible) */}
      <div style={{ marginBottom: '1rem' }}>
        <button type="button" onClick={() => setShowAgent(!showAgent)}
          style={{ ...styles.linkBtn, fontSize: '0.875rem' }}>
          {showAgent ? '- Hide' : '+ Add'} Real Estate Agent Info
        </button>
        {showAgent && (
          <fieldset style={styles.fieldset}>
            <div style={styles.formRow}>
              <div style={styles.formField}>
                <label style={labelStyle}>Agent Name</label>
                <input style={inputStyle} value={form.agentName}
                  onChange={e => handleChange('agentName', e.target.value)} />
              </div>
              <div style={styles.formField}>
                <label style={labelStyle}>Agent Email</label>
                <input style={inputStyle} type="email" value={form.agentEmail}
                  onChange={e => handleChange('agentEmail', e.target.value)} />
              </div>
              <div style={styles.formField}>
                <label style={labelStyle}>Agent Phone</label>
                <input style={inputStyle} type="tel" value={form.agentPhone}
                  onChange={e => handleChange('agentPhone', e.target.value)} />
              </div>
            </div>
          </fieldset>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <button type="button" onClick={onCancel} style={styles.btnSecondary}>Cancel</button>
        <button type="submit" disabled={saving}
          style={{ ...styles.btn, backgroundColor: 'var(--color-primary-600, #2563EB)', color: '#fff', opacity: saving ? 0.6 : 1 }}>
          {saving ? 'Booking...' : 'Book Appointment'}
        </button>
      </div>
    </form>
  );
}

// ============================================================
// STYLES (inline for portability + responsiveness via media queries in CSS)
// ============================================================
const styles = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '0.75rem',
    marginBottom: '1rem'
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    flexWrap: 'wrap'
  },
  headerRight: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center'
  },
  viewToggle: {
    display: 'flex',
    borderRadius: '6px',
    overflow: 'hidden',
    border: '1px solid var(--color-border, #E5E7EB)'
  },
  viewBtn: {
    padding: '0.375rem 0.75rem',
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.8125rem',
    fontWeight: 500,
    transition: 'all 0.15s'
  },
  navBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1rem',
    marginBottom: '1rem'
  },
  navBtn: {
    padding: '0.5rem 0.75rem',
    border: '1px solid var(--color-border, #E5E7EB)',
    borderRadius: '6px',
    backgroundColor: 'var(--surface-card, #fff)',
    cursor: 'pointer',
    fontSize: '1.125rem',
    lineHeight: 1
  },
  todayBtn: {
    padding: '0.375rem 0.75rem',
    border: '1px solid var(--color-border, #E5E7EB)',
    borderRadius: '6px',
    backgroundColor: 'var(--surface-card, #fff)',
    cursor: 'pointer',
    fontSize: '0.8125rem',
    fontWeight: 500
  },
  calendarGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '1px',
    backgroundColor: 'var(--color-border, #E5E7EB)',
    border: '1px solid var(--color-border, #E5E7EB)',
    borderRadius: '8px',
    overflow: 'hidden',
    minWidth: '320px'
  },
  calendarDayHeader: {
    padding: '0.5rem',
    textAlign: 'center',
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    backgroundColor: 'var(--surface-page, #F9FAFB)',
    color: 'var(--text-secondary, #6B7280)'
  },
  calendarCell: {
    minHeight: '80px',
    padding: '0.25rem',
    backgroundColor: 'var(--surface-card, #fff)',
    border: '1px solid transparent',
    transition: 'background-color 0.15s'
  },
  calendarDateNum: {
    fontSize: '0.8125rem',
    padding: '2px 4px'
  },
  calendarEvents: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
    marginTop: '2px'
  },
  calendarEventDot: {
    fontSize: '0.65rem',
    padding: '1px 4px',
    borderRadius: '3px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    cursor: 'pointer'
  },
  dayViewContainer: {
    border: '1px solid var(--color-border, #E5E7EB)',
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: 'var(--surface-card, #fff)'
  },
  dayTimeline: {
    minHeight: '400px'
  },
  dayHourRow: {
    display: 'flex',
    borderBottom: '1px solid var(--color-border, #E5E7EB)',
    minHeight: '60px'
  },
  dayHourLabel: {
    width: '70px',
    flexShrink: 0,
    padding: '0.5rem',
    fontSize: '0.75rem',
    color: 'var(--text-secondary, #6B7280)',
    borderRight: '1px solid var(--color-border, #E5E7EB)',
    textAlign: 'right'
  },
  dayHourSlot: {
    flex: 1,
    padding: '0.25rem 0.5rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem'
  },
  dayEventBlock: {
    padding: '0.5rem',
    borderRadius: '4px',
    borderLeft: '3px solid',
    cursor: 'pointer',
    fontSize: '0.8125rem'
  },
  listContainer: {
    border: '1px solid var(--color-border, #E5E7EB)',
    borderRadius: '8px',
    overflow: 'auto',
    backgroundColor: 'var(--surface-card, #fff)'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.875rem'
  },
  th: {
    padding: '0.75rem',
    textAlign: 'left',
    borderBottom: '2px solid var(--color-border, #E5E7EB)',
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    color: 'var(--text-secondary, #6B7280)'
  },
  thHideMobile: {
    padding: '0.75rem',
    textAlign: 'left',
    borderBottom: '2px solid var(--color-border, #E5E7EB)',
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    color: 'var(--text-secondary, #6B7280)'
  },
  tr: {
    borderBottom: '1px solid var(--color-border, #E5E7EB)',
    transition: 'background-color 0.1s'
  },
  td: {
    padding: '0.75rem'
  },
  tdHideMobile: {
    padding: '0.75rem'
  },
  badge: {
    padding: '0.25rem 0.5rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: 500,
    border: '1px solid',
    whiteSpace: 'nowrap'
  },
  btn: {
    padding: '0.5rem 1rem',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: 500
  },
  btnSmall: {
    padding: '0.25rem 0.5rem',
    border: '1px solid var(--color-primary-600, #2563EB)',
    borderRadius: '4px',
    backgroundColor: 'transparent',
    color: 'var(--color-primary-600, #2563EB)',
    cursor: 'pointer',
    fontSize: '0.75rem',
    fontWeight: 500
  },
  btnSecondary: {
    padding: '0.5rem 1rem',
    border: '1px solid var(--color-border, #E5E7EB)',
    borderRadius: '6px',
    backgroundColor: 'var(--surface-card, #fff)',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: 500
  },
  linkBtn: {
    background: 'none',
    border: 'none',
    color: 'var(--color-primary-600, #2563EB)',
    cursor: 'pointer',
    padding: '0.25rem 0',
    fontWeight: 500
  },
  emptyState: {
    textAlign: 'center',
    padding: '3rem',
    border: '2px dashed var(--color-border, #E5E7EB)',
    borderRadius: '8px',
    color: 'var(--text-secondary, #666)'
  },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1rem'
  },
  detailGroup: {
    marginBottom: '0.5rem'
  },
  detailLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    color: 'var(--text-secondary, #6B7280)',
    marginBottom: '0.25rem',
    display: 'block'
  },
  fieldset: {
    border: '1px solid var(--color-border, #E5E7EB)',
    borderRadius: '6px',
    padding: '0.75rem',
    marginBottom: '1rem'
  },
  legend: {
    fontWeight: 600,
    fontSize: '0.875rem',
    padding: '0 0.5rem'
  },
  formRow: {
    display: 'flex',
    gap: '0.75rem',
    flexWrap: 'wrap'
  },
  formField: {
    flex: 1,
    minWidth: '140px',
    marginBottom: '0.5rem'
  },
  formLabel: {
    display: 'block',
    fontSize: '0.75rem',
    fontWeight: 500,
    marginBottom: '0.25rem',
    color: 'var(--text-secondary, #6B7280)'
  },
  input: {
    width: '100%',
    padding: '0.5rem',
    border: '1px solid var(--color-border, #E5E7EB)',
    borderRadius: '6px',
    fontSize: '0.875rem',
    backgroundColor: 'var(--surface-card, #fff)',
    color: 'var(--text-primary, #111)',
    boxSizing: 'border-box'
  }
};

export default SchedulePage;
